<template>
  <section v-home-reveal class="stock" :aria-label="t('homeStockLooksTitle')">
    <div class="shots">
      <figure v-for="(look, index) in looks" :key="look.titleKey" :class="'shot-' + index">
        <img :src="look.src" :alt="t(look.titleKey)" />
        <figcaption>{{ t(look.titleKey) }}</figcaption>
      </figure>
    </div>
    <div class="copy">
      <p class="kicker">{{ t('homeStockLooksKicker') }}</p>
      <h2>{{ t('homeStockLooksTitle') }}</h2>
      <p>{{ t('homeStockLooksText') }}</p>
    </div>
  </section>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { HOME_STOCK_LOOKS } from './home.data';
import { vHomeReveal } from './home-reveal';

export default defineComponent({
  directives: {
    homeReveal: vHomeReveal,
  },
  setup() {
    const { t } = useI18n();

    return {
      t,
      looks: HOME_STOCK_LOOKS,
    };
  },
});
</script>
<style scoped lang="scss">
.stock {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 12px 28px;
  margin: 8px 0 72px;
  align-items: center;
  overflow: visible;
  text-align: left;
}

.kicker {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(246, 243, 238, 0.62);
}

.kicker::before {
  content: '';
  width: 28px;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(90deg, #fa496e, #a67eff, #52dbff);
}

h2 {
  margin: 0 0 12px;
  font-size: clamp(1.55rem, 3.2vw, 2.35rem);
  font-weight: 600;
  letter-spacing: -0.04em;
}

.copy p:last-child {
  margin: 0;
  max-width: 36ch;
  font-size: 1rem;
  line-height: 1.55;
  color: rgba(246, 243, 238, 0.76);
}

.shots {
  position: relative;
  isolation: isolate;
  height: min(52vw, 440px);
  min-height: 360px;
  margin-right: -6%;
  overflow: visible;
}

figure {
  position: absolute;
  width: 46%;
  margin: 0;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(246, 243, 238, 0.22);
  aspect-ratio: 4 / 5;
  box-shadow: 0 22px 44px rgba(8, 6, 12, 0.45);
  transform-origin: 50% 80%;
  transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1);
}

.shot-0 {
  left: 0;
  top: 12%;
  z-index: 1;
  transform: rotate(-12deg);
}

.shot-1 {
  left: 21%;
  top: 18%;
  z-index: 2;
  transform: rotate(-2deg);
}

.shot-2 {
  left: 42%;
  top: 6%;
  width: 50%;
  z-index: 3;
  transform: rotate(7deg);
}

.shots:hover .shot-0 {
  transform: rotate(-16deg) translateX(-4%);
}

.shots:hover .shot-1 {
  transform: rotate(-1deg) translateY(-8px);
}

.shots:hover .shot-2 {
  transform: rotate(10deg) translateX(4%);
}

img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.shot-0 img {
  object-position: 72% 28%;
}

.shot-1 img {
  object-position: 42% 28%;
}

.shot-2 img {
  object-position: 68% 22%;
}

figcaption {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f6f3ee;
  background: rgba(20, 16, 24, 0.58);
  backdrop-filter: blur(8px);
}

@media (max-width: 800px) {
  .stock {
    grid-template-columns: 1fr;
    gap: 8px;
    margin: 8px 0 48px;
  }

  .copy {
    order: -1;
  }

  .shots {
    height: 320px;
    min-height: 300px;
    margin-right: 0;
  }

  figure {
    width: 54%;
  }

  .shot-0 {
    left: 2%;
    top: 10%;
    transform: rotate(-10deg);
  }

  .shot-1 {
    left: 22%;
    top: 16%;
  }

  .shot-2 {
    left: 40%;
    width: 56%;
    transform: rotate(6deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  figure,
  .shots:hover .shot-0,
  .shots:hover .shot-1,
  .shots:hover .shot-2 {
    transition: none;
  }

  .shots:hover .shot-0 {
    transform: rotate(-12deg);
  }

  .shots:hover .shot-1 {
    transform: rotate(-2deg);
  }

  .shots:hover .shot-2 {
    transform: rotate(7deg);
  }
}
</style>
